import { useState } from 'react';
import { clsx } from '../../ds/clsx';
import { Button, Sheet, Toggle } from '../../ds/components';
import { WEEKDAY_LONG, normalizeTime } from './dates';
import {
  MAX_WINDOWS,
  windowsText,
  type AvailabilityForm,
  type DayState,
  type WindowState,
} from './availabilityForm';
import styles from './AvailabilityGrid.module.css';

interface TimeInputProps {
  label: string;
  value: string;
  disabled?: boolean | undefined;
  invalid?: boolean | undefined;
  onChange: (value: string) => void;
  onCommit: (value: string) => void;
  className?: string | undefined;
}

/** Text field for "HH:MM" (Beratung 08: 74 px, centred, tabular); normalises on blur. */
export function TimeInput({
  label,
  value,
  disabled,
  invalid,
  onChange,
  onCommit,
  className,
}: TimeInputProps) {
  return (
    <input
      type="text"
      inputMode="numeric"
      autoComplete="off"
      aria-label={label}
      aria-invalid={invalid || undefined}
      className={clsx(styles.time, invalid && styles.timeInvalid, className)}
      value={value}
      disabled={disabled}
      placeholder="—"
      onChange={(event) => onChange(event.target.value)}
      onBlur={(event) => onCommit(normalizeTime(event.target.value) ?? event.target.value.trim())}
    />
  );
}

export interface AvailabilityGridProps {
  form: AvailabilityForm;
  /** `sheet` = Beratung 08 rows (Mo/Di, two window pairs), `settings` = Einstellungen 14 rows, `mobile` = EM6 list. */
  variant: 'sheet' | 'settings' | 'mobile';
  disabled?: boolean | undefined;
  /** Called after a change that the settings page saves automatically (toggle, committed time). */
  onCommit?: (() => void) | undefined;
}

function longLabel(day: DayState): string {
  return WEEKDAY_LONG[day.weekday - 1] ?? day.label;
}

/** Beratung 08 row: label · toggle · "09:00 – 13:00 · 14:00 – 18:30" with dashed placeholders for the second pair. */
function SheetRow({
  day,
  form,
  disabled,
  onCommit,
}: {
  day: DayState;
  form: AvailabilityForm;
  disabled: boolean;
  onCommit: (() => void) | undefined;
}) {
  const error = form.errors[day.weekday];
  const pairs: Array<WindowState | null> = [day.windows[0] ?? null, day.windows[1] ?? null];
  return (
    <div
      className={clsx(styles.sheetRow, !day.enabled && styles.sheetRowOff)}
      role="group"
      aria-label={longLabel(day)}
    >
      <span className={styles.sheetLabel}>{day.label}</span>
      <Toggle
        label={`${longLabel(day)} buchbar`}
        labelHidden
        size="large"
        className={styles.sheetToggle}
        checked={day.enabled}
        disabled={disabled}
        onChange={(checked) => {
          form.setEnabled(day.weekday, checked);
          onCommit?.();
        }}
      />
      {day.enabled ? (
        <span className={styles.sheetWindows}>
          {pairs.map((pair, index) =>
            pair ? (
              <span key={index} className={styles.pair}>
                {index > 0 ? <span className={styles.dot}>·</span> : null}
                <TimeInput
                  label={`${longLabel(day)} · Beginn ${index + 1}`}
                  value={pair.start}
                  disabled={disabled}
                  invalid={Boolean(error)}
                  onChange={(value) => form.setWindow(day.weekday, index, 'start', value)}
                  onCommit={(value) => {
                    form.setWindow(day.weekday, index, 'start', value);
                    onCommit?.();
                  }}
                />
                <span className={styles.dash}>–</span>
                <TimeInput
                  label={`${longLabel(day)} · Ende ${index + 1}`}
                  value={pair.end}
                  disabled={disabled}
                  invalid={Boolean(error)}
                  onChange={(value) => form.setWindow(day.weekday, index, 'end', value)}
                  onCommit={(value) => {
                    form.setWindow(day.weekday, index, 'end', value);
                    onCommit?.();
                  }}
                />
                {index > 0 ? (
                  <button
                    type="button"
                    className={styles.removeWindow}
                    aria-label={`${longLabel(day)} · Zeitfenster ${index + 1} entfernen`}
                    disabled={disabled}
                    onClick={() => {
                      form.removeWindow(day.weekday, index);
                      onCommit?.();
                    }}
                  >
                    ✕
                  </button>
                ) : null}
              </span>
            ) : (
              <span key={index} className={styles.pair}>
                {index > 0 ? <span className={styles.dot}>·</span> : null}
                <button
                  type="button"
                  className={styles.placeholder}
                  aria-label={`${longLabel(day)} · Zeitfenster ${index + 1} hinzufügen`}
                  disabled={disabled || day.windows.length >= MAX_WINDOWS}
                  onClick={() => form.addWindow(day.weekday)}
                >
                  —
                </button>
                <span className={styles.dash}>–</span>
                <span className={clsx(styles.placeholder, styles.placeholderStatic)} aria-hidden="true">
                  —
                </span>
              </span>
            ),
          )}
        </span>
      ) : (
        <span className={styles.offText}>Nicht buchbar</span>
      )}
      {error ? <span className={styles.rowError}>{error}</span> : null}
    </div>
  );
}

/** Einstellungen 14 row: toggle · Montag · 09:00 bis 18:00 [· 14:00 bis 19:00] · „+ Zeitfenster". */
function SettingsRow({
  day,
  form,
  disabled,
  onCommit,
}: {
  day: DayState;
  form: AvailabilityForm;
  disabled: boolean;
  onCommit: (() => void) | undefined;
}) {
  const error = form.errors[day.weekday];
  return (
    <div
      className={clsx(
        styles.settingsRow,
        !day.enabled && styles.settingsRowOff,
        error && styles.settingsRowError,
      )}
      role="group"
      aria-label={longLabel(day)}
    >
      <Toggle
        label={`${longLabel(day)} verfügbar`}
        labelHidden
        size="large"
        checked={day.enabled}
        disabled={disabled}
        onChange={(checked) => {
          form.setEnabled(day.weekday, checked);
          onCommit?.();
        }}
      />
      <span className={styles.settingsLabel}>{longLabel(day)}</span>
      {day.enabled ? (
        <>
          {day.windows.map((window, index) => (
            <span key={index} className={styles.settingsPair}>
              <TimeInput
                label={`${longLabel(day)} · Beginn ${index + 1}`}
                value={window.start}
                disabled={disabled}
                invalid={Boolean(error)}
                className={styles.settingsTime}
                onChange={(value) => form.setWindow(day.weekday, index, 'start', value)}
                onCommit={(value) => {
                  form.setWindow(day.weekday, index, 'start', value);
                  onCommit?.();
                }}
              />
              <span className={styles.bis}>bis</span>
              <TimeInput
                label={`${longLabel(day)} · Ende ${index + 1}`}
                value={window.end}
                disabled={disabled}
                invalid={Boolean(error)}
                className={styles.settingsTime}
                onChange={(value) => form.setWindow(day.weekday, index, 'end', value)}
                onCommit={(value) => {
                  form.setWindow(day.weekday, index, 'end', value);
                  onCommit?.();
                }}
              />
              {index > 0 ? (
                <button
                  type="button"
                  className={styles.removeWindow}
                  aria-label={`${longLabel(day)} · Zeitfenster ${index + 1} entfernen`}
                  disabled={disabled}
                  onClick={() => {
                    form.removeWindow(day.weekday, index);
                    onCommit?.();
                  }}
                >
                  ✕
                </button>
              ) : null}
            </span>
          ))}
          {day.windows.length < MAX_WINDOWS ? (
            <button
              type="button"
              className={styles.addWindow}
              disabled={disabled}
              onClick={() => form.addWindow(day.weekday)}
            >
              + Zeitfenster
            </button>
          ) : null}
        </>
      ) : (
        <span className={styles.settingsOffText}>Nicht verfügbar</span>
      )}
      {error ? <span className={styles.rowError}>{error}</span> : null}
    </div>
  );
}

/** EM6 row: toggle · Mo · „09:00 – 18:00"; tapping the times opens the day sheet. */
function MobileRow({
  day,
  form,
  disabled,
  onCommit,
  onEdit,
}: {
  day: DayState;
  form: AvailabilityForm;
  disabled: boolean;
  onCommit: (() => void) | undefined;
  onEdit: () => void;
}) {
  const error = form.errors[day.weekday];
  return (
    <div
      className={clsx(styles.mobileRow, !day.enabled && styles.mobileRowOff, error && styles.mobileRowError)}
    >
      <Toggle
        label={`${longLabel(day)} verfügbar`}
        labelHidden
        size="large"
        checked={day.enabled}
        disabled={disabled}
        onChange={(checked) => {
          form.setEnabled(day.weekday, checked);
          onCommit?.();
        }}
      />
      <span className={styles.mobileLabel}>{day.label}</span>
      {day.enabled ? (
        <button type="button" className={styles.mobileTimes} disabled={disabled} onClick={onEdit}>
          {windowsText(day) || 'Zeiten wählen'}
        </button>
      ) : (
        <span className={styles.mobileOffText}>Nicht verfügbar</span>
      )}
      {error ? <span className={styles.rowError}>{error}</span> : null}
    </div>
  );
}

/** The week grid of the availability in its three layouts. */
export function AvailabilityGrid({ form, variant, disabled = false, onCommit }: AvailabilityGridProps) {
  const [editing, setEditing] = useState<DayState | null>(null);
  const editingCurrent = editing ? (form.days.find((day) => day.weekday === editing.weekday) ?? null) : null;

  if (variant === 'mobile') {
    return (
      <>
        <div className={styles.mobileList}>
          {form.days.map((day) => (
            <MobileRow
              key={day.weekday}
              day={day}
              form={form}
              disabled={disabled}
              onCommit={onCommit}
              onEdit={() => setEditing(day)}
            />
          ))}
        </div>
        <Sheet
          open={editingCurrent !== null}
          onClose={() => setEditing(null)}
          title={editingCurrent ? longLabel(editingCurrent) : undefined}
          subtitle="Zeitfenster für diesen Tag"
          footer={
            <Button
              onClick={() => {
                setEditing(null);
                onCommit?.();
              }}
            >
              Übernehmen
            </Button>
          }
        >
          {editingCurrent ? (
            <div className={styles.sheetEditor}>
              {editingCurrent.windows.map((window, index) => (
                <div key={index} className={styles.sheetEditorRow}>
                  <TimeInput
                    label={`Beginn ${index + 1}`}
                    value={window.start}
                    className={styles.sheetEditorTime}
                    onChange={(value) => form.setWindow(editingCurrent.weekday, index, 'start', value)}
                    onCommit={(value) => form.setWindow(editingCurrent.weekday, index, 'start', value)}
                  />
                  <span className={styles.bis}>bis</span>
                  <TimeInput
                    label={`Ende ${index + 1}`}
                    value={window.end}
                    className={styles.sheetEditorTime}
                    onChange={(value) => form.setWindow(editingCurrent.weekday, index, 'end', value)}
                    onCommit={(value) => form.setWindow(editingCurrent.weekday, index, 'end', value)}
                  />
                  {index > 0 ? (
                    <button
                      type="button"
                      className={styles.removeWindow}
                      aria-label={`Zeitfenster ${index + 1} entfernen`}
                      onClick={() => form.removeWindow(editingCurrent.weekday, index)}
                    >
                      ✕
                    </button>
                  ) : null}
                </div>
              ))}
              {editingCurrent.windows.length < MAX_WINDOWS ? (
                <button
                  type="button"
                  className={styles.addWindow}
                  onClick={() => form.addWindow(editingCurrent.weekday)}
                >
                  + Zeitfenster
                </button>
              ) : null}
              {form.errors[editingCurrent.weekday] ? (
                <span className={styles.rowError}>{form.errors[editingCurrent.weekday]}</span>
              ) : null}
            </div>
          ) : null}
        </Sheet>
      </>
    );
  }

  if (variant === 'settings') {
    return (
      <div className={styles.settingsList}>
        {form.days.map((day) => (
          <SettingsRow key={day.weekday} day={day} form={form} disabled={disabled} onCommit={onCommit} />
        ))}
      </div>
    );
  }

  return (
    <div className={styles.sheetList}>
      {form.days.map((day) => (
        <SheetRow key={day.weekday} day={day} form={form} disabled={disabled} onCommit={onCommit} />
      ))}
    </div>
  );
}
